import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { fetchGlobalSearchResults } from "../../utils/globalSearchApi";

const MIN_CHARS = 2;

function GlobalSearch() {
  const history = useHistory();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ ponds: [], pondDetails: [], staff: [] });
  const [activeIndex, setActiveIndex] = useState(-1);

  const flatItems = React.useMemo(() => {
    const items = [];
    results.ponds.forEach((pond) => {
      items.push({
        type: "pond",
        id: pond.id,
        title: pond.Name || "Unnamed pond",
        subtitle: pond.Location || pond.FisherName || "",
        path: `/updatePond/${pond.id}`,
      });
    });
    results.pondDetails.forEach((detail) => {
      items.push({
        type: "pondDetail",
        id: detail.id,
        title: detail.Name || "Unnamed sampling data",
        subtitle: detail.PondName ? `Pond: ${detail.PondName}` : "",
        path: `/UpdatePD/${detail.id}`,
      });
    });
    results.staff.forEach((member) => {
      items.push({
        type: "staff",
        id: member.id,
        title: member.FullName || "Team member",
        subtitle: member.Email || "",
        path: `/UpdateStaff/${member.id}`,
      });
    });
    return items;
  }, [results]);

  const hasResults = flatItems.length > 0;
  const showPanel = open && query.trim().length >= MIN_CHARS;

  const runSearch = useCallback(async (term) => {
    const trimmed = term.trim();
    if (trimmed.length < MIN_CHARS) {
      setResults({ ponds: [], pondDetails: [], staff: [] });
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const data = await fetchGlobalSearchResults(trimmed);
      if (requestId !== requestIdRef.current) return;
      setResults(data);
      setActiveIndex(-1);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < MIN_CHARS) {
      setResults({ ponds: [], pondDetails: [], staff: [] });
      setLoading(false);
      return undefined;
    }
    debounceRef.current = setTimeout(() => runSearch(query), 800);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const navigateTo = (path) => {
    setOpen(false);
    setQuery("");
    setResults({ ponds: [], pondDetails: [], staff: [] });
    history.push(path);
  };

  const onKeyDown = (e) => {
    if (!showPanel) return;

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!hasResults) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? flatItems.length - 1 : prev - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      navigateTo(flatItems[activeIndex].path);
    }
  };

  const typeLabel = (type) => {
    if (type === "pond") return "Pond";
    if (type === "pondDetail") return "Sampling data";
    return "Team";
  };

  const typeIcon = (type) => {
    if (type === "pond") return "fa-tint";
    if (type === "pondDetail") return "fa-line-chart";
    return "fa-user";
  };

  let sectionOffset = 0;
  const sections = [
    { key: "ponds", label: "Ponds", items: results.ponds },
    { key: "pondDetails", label: "Fishing / Sampling Data", items: results.pondDetails },
    { key: "staff", label: "Team", items: results.staff },
  ].filter((section) => section.items.length > 0);

  return (
    <div className="donezo-header-search" ref={rootRef}>
      <i className="fa fa-search donezo-search-icon" aria-hidden />
      <input
        ref={inputRef}
        type="search"
        className="form-control"
        placeholder="Search ponds, sampling data, team…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        aria-label="Global search"
        aria-expanded={showPanel}
        aria-controls="global-search-results"
        role="combobox"
        aria-autocomplete="list"
      />

      {showPanel && (
        <div
          id="global-search-results"
          className="donezo-global-search-panel"
          role="listbox"
        >
          {loading && (
            <div className="donezo-global-search-status">
              <i className="fa fa-spinner fa-spin me-2" aria-hidden />
              Searching…
            </div>
          )}

          {!loading && !hasResults && (
            <div className="donezo-global-search-status">No results for &ldquo;{query.trim()}&rdquo;</div>
          )}

          {!loading &&
            sections.map((section) => {
              const startIndex = sectionOffset;
              sectionOffset += section.items.length;
              return (
                <div key={section.key} className="donezo-global-search-group">
                  <div className="donezo-global-search-group-label">{section.label}</div>
                  {section.items.map((raw, i) => {
                    const item = flatItems[startIndex + i];
                    const isActive = activeIndex === startIndex + i;
                    return (
                      <button
                        key={`${item.type}-${item.id}`}
                        type="button"
                        className={`donezo-global-search-item${isActive ? " is-active" : ""}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveIndex(startIndex + i)}
                        onClick={() => navigateTo(item.path)}
                      >
                        <span className="donezo-global-search-item-icon" aria-hidden>
                          <i className={`fa ${typeIcon(item.type)}`} />
                        </span>
                        <span className="donezo-global-search-item-body">
                          <span className="donezo-global-search-item-title">{item.title}</span>
                          {item.subtitle ? (
                            <span className="donezo-global-search-item-subtitle">{item.subtitle}</span>
                          ) : null}
                        </span>
                        <span className="donezo-global-search-item-type">{typeLabel(item.type)}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
